import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { API_TIMEOUT_MS, apiBaseUrl } from './baseUrl'
import { refreshAccessToken } from '@/shared/auth/sessionRefresh'
import { clearSession, getToken, isTokenExpired } from '@/shared/auth/tokenStorage'

export const api = axios.create({
  baseURL: apiBaseUrl,
  headers: { 'Content-Type': 'application/json' },
  timeout: API_TIMEOUT_MS,
})

// Har so'rovga token qo'shiladi
api.interceptors.request.use((config) => {
  const token = getToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

/**
 * Backend javobining yagona shakli (`uz.xsib.common.response.XsibResponse`).
 *
 * ⚠️ Bu tipni sahifalar KO'RMASLIGI kerak — quyidagi interceptor uni
 * ochib tashlaydi va chaqiruvchiga faqat `data` boradi.
 */
export interface XsibEnvelope<T> {
  success: boolean
  requestId: string | null
  timeZone: string | null
  error: { code: number | null; tag: string | null; text: string | null } | null
  data: T
}

function isEnvelope(body: unknown): body is XsibEnvelope<unknown> {
  return typeof body === 'object' && body !== null && 'success' in body && 'data' in body
}

/**
 * Envelope ichidagi xato — `translateError` uni `message` orqali o'qiydi.
 *
 * `tag` saqlanadi: u backend'dagi barqaror belgi (`ILLEGAL_TRANSITION`,
 * `CONSTRAINT_23505`) va shartni MATN bo'yicha emas, shu bo'yicha yozish
 * kerak — matn tarjima qilinadi va o'zgaradi.
 */
export class XsibApiError extends Error {
  constructor(
    message: string,
    readonly tag: string | null,
    readonly code: number | null,
    readonly requestId: string | null,
  ) {
    super(message)
    this.name = 'XsibApiError'
  }
}

/**
 * Envelope BITTA joyda ochiladi.
 *
 * ⚠️ NEGA HAR CHAQIRUVDA EMAS. Backend endi har javobni
 * `{success, requestId, timeZone, error, data}` ichiga soladi. Buni har
 * chaqiruvda (`res.data.data`) yozish 200 dan ortiq joyda takrorlanardi va
 * bittasini unutish yetarli edi — unutilgani esa faqat ekranda
 * `undefined` chiqqanda bilinardi, ya'ni ishlab chiqarishda.
 *
 * ⚠️ Zod sxemalari TEGILMAYDI: ular allaqachon ichki (`data`) shaklni
 * tekshiradi, va bu interceptor ularga aynan o'shani beradi.
 *
 * ⚠️ Envelope bo'lmagan javob (masalan proksi qaytargan HTML xato
 * sahifasi) o'z holicha o'tadi — bu yerda uni "tuzatishga" urinish
 * haqiqiy sababni yashirardi.
 *
 * `success === false` bilan kelgan 2xx javobi — mantiqiy nomuvofiqlik:
 * backend qoidasi bo'yicha xatoda status kodi ham xato bo'lishi kerak.
 * Baribir rad etamiz, aks holda sahifa bo'sh `data` ni haqiqiy
 * ma'lumot deb qabul qilardi.
 */
api.interceptors.response.use((response) => {
  const body: unknown = response.data
  if (isEnvelope(body)) {
    if (!body.success) {
      return Promise.reject(
        new XsibApiError(
          // ⚠️ Bu yerda QOTIRILGAN o'zbekcha matn TURMASIN: u `translateError`
          // orqali to'g'ridan-to'g'ri ekranga chiqadi va til almashtirilganda
          // ham o'zbekcha qolardi. Bo'sh satr `errors.generic` ga tushadi.
          body.error?.text ?? '',
          body.error?.tag ?? null,
          body.error?.code ?? null,
          body.requestId,
        ),
      )
    }
    response.data = body.data
  }
  return response
})

/** Bir marta takrorlanganini belgilash uchun — quyidagi izohga qarang. */
interface RetriableConfig extends InternalAxiosRequestConfig {
  __xsibRetried?: boolean
}

/**
 * Kirish oqimining o'z yo'llari.
 *
 * ⚠️ Ular 401 qaytarganda tokenni yangilashga URINILMAYDI: bu yerdagi 401
 * "parol/TOTP/refresh noto'g'ri" degani, "sessiya eskirgan" degani emas.
 * `/api/auth/refresh` ning o'zi ham shu ro'yxatda — u 401 bersa uni yana
 * yangilash aynan o'sha cheksiz sikl bo'lardi. (Amalda `sessionRefresh.ts`
 * boshqa, interceptorsiz axios nusxasidan foydalanadi, ya'ni bu yerga
 * umuman kelmaydi; ro'yxat — ikkinchi qavat himoya.)
 */
const AUTH_FLOW_PATHS = [
  '/api/auth/login',
  '/api/auth/totp/',
  '/api/auth/refresh',
  '/api/auth/logout',
]

function isAuthFlowPath(url: string): boolean {
  return AUTH_FLOW_PATHS.some((path) => url.includes(path))
}

/**
 * 401 sababi SESSIYA ekanini aniqlaydi.
 *
 * Ikkita mustaqil signal, ikkalasi ham kerak:
 *   1. `tag === 'UNAUTHORIZED'` — gateway'ning O'ZI rad etgan
 *      (`GatewayResponses.unauthorized`: "Token berilmagan" /
 *      "Token yaroqsiz yoki muddati o'tgan"). Eng ishonchli signal, chunki
 *      u faqat token tekshiruvidan chiqadi.
 *   2. mahalliy muddat o'tgan yoki token umuman yo'q — gateway chetlab
 *      o'tilgan muhitlar (dev'da to'g'ridan-to'g'ri servisga) uchun zaxira.
 *
 * ⚠️ NEGA "HAR QANDAY 401 DA YANGILAYMIZ" EMAS. Xizmat 401 ni sessiyaga
 * aloqasi yo'q sabablardan ham qaytaradi: `/error` dispatch'i yopiq
 * bo'lgani uchun noto'g'ri UUID, bean validatsiya xatosi va noma'lum yo'l
 * ham TANASIZ 401 bo'lib keladi. Ularda yangilash — keraksiz aylantirish
 * va, undan ham yomoni, so'rovning TAKRORLANISHI (POST ikki marta
 * ketardi).
 */
function looksLikeExpiredSession(error: AxiosError): boolean {
  const body = error.response?.data as { error?: { tag?: string | null } | null } | undefined
  if (body?.error?.tag === 'UNAUTHORIZED') return true
  return getToken() === null || isTokenExpired()
}

function forceLogout(): void {
  const onLoginPage = window.location.pathname === '/login'
  clearSession()
  if (!onLoginPage) {
    window.location.replace('/login')
  }
}

/**
 * 401 → AVVAL tokenni yangilashga urinish, faqat undan keyin chiqarish.
 *
 * <h2>Nima uchun umuman kerak</h2>
 * Access token 30 daqiqa, refresh token 14 kun yashaydi. Backend
 * `POST /api/auth/refresh` ni bergan, lekin panel uni ISHLATMASDI — ya'ni
 * 14 kunlik imkoniyat bor edi va operator baribir har 30 daqiqada kirish
 * sahifasiga uchib ketardi. Foydalanuvchi shikoyatining sababi shu.
 *
 * <h2>⚠️ SO'ROV FAQAT BIR MARTA TAKRORLANADI</h2>
 * `__xsibRetried` — cheksiz siklga qarshi. Yangilash muvaffaqiyatli
 * bo'lib, so'rov YANA 401 olsa (masalan huquq yo'q yoki bu umuman
 * sessiyaga aloqasi yo'q 401 edi) — ikkinchi urinish BO'LMAYDI, xato
 * chaqiruvchiga boradi. Bu shart bo'lmasa, doim 401 qaytaradigan bitta
 * endpoint butun sessiyani cheksiz aylantirishga majbur qilardi.
 *
 * <h2>⚠️ 401 NING HAMMASI SESSIYA HAQIDA EMAS</h2>
 * (`looksLikeExpiredSession` izohi.) Qolgan 401 chaqiruvchiga oddiy xato
 * bo'lib boradi — "loginni bo'sh qoldirib yaratish" tugmasini bosgan
 * operator validatsiya xatosini ko'rishi kerak, kirish sahifasini emas.
 *
 * <h2>403 bu yerda USHLANMAYDI</h2>
 * U "kirgansiz, lekin huquq yo'q" degani; chiqarish noto'g'ri bo'lardi.
 *
 * <h2>⚠️ KIRISH OQIMI CHIQARILMAYDI — XSB-005</h2>
 * Bu yerdagi oxirgi qator (`getToken() === null` bo'lsa chiqarish) kirish
 * oqimini ham qamrab olardi va oqibati o'lchangan (Staging, 2026-08-13):
 * TOTP ekranida noto'g'ri kod kiritilganda `/api/auth/totp/confirm` 401
 * qaytaradi, TOTP paytida esa sessiya tokeni hali YO'Q — ya'ni shart
 * har doim rost bo'lib, panel `/login` ga TO'LIQ QAYTA YUKLANARDI.
 * `TotpPage` ning xato bannerini ko'rsatadigan `catch` i ishga tushib
 * ulgurmasdi (sahifa allaqachon almashgan), foydalanuvchi esa nima
 * bo'lganini bilmay loginni qaytadan terardi.
 *
 * ⚠️ Bu paroldagi 401 da SEZILMASDI: u paytda foydalanuvchi allaqachon
 * `/login` da turadi va `forceLogout` o'sha sahifada qayta
 * yo'naltirmaydi. Nuqson faqat `/login/totp` da ko'rindi.
 *
 * Server tomonda hech narsa buzuq emas va u ATAYLAB shunday: oqim tokeni
 * noto'g'ri kodda SARFLANMAYDI (`LoginFlowService#verifyTotp` —
 * `flowTokens.consume` faqat kod to'g'ri bo'lganda chaqiriladi), ya'ni
 * qayta urinish server uchun kutilgan holat. Sessiya faqat 5 urinishdan
 * keyin (`totp_locked_until`) yoki oqim tokeni muddati o'tganda yopiladi
 * va ikkalasida ham server AYNAN shu sababni matn bilan qaytaradi.
 */
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status !== 401) return Promise.reject(error)

    const request = error.config as RetriableConfig | undefined
    const retriable =
      request !== undefined &&
      request.__xsibRetried !== true &&
      !isAuthFlowPath(request.url ?? '') &&
      looksLikeExpiredSession(error)

    if (retriable) {
      request.__xsibRetried = true
      // ⚠️ Yangilash BITTA navbatda ketadi — parallel so'rovlarning
      // hammasi shu yerga kelsa ham serverga bitta so'rov boradi
      // (`sessionRefresh.ts`). Aks holda ular bir-birining refresh
      // tokenini o'ldirardi.
      const outcome = await refreshAccessToken(getToken())

      if (outcome.status === 'refreshed') {
        request.headers.Authorization = `Bearer ${outcome.token}`
        return api.request(request)
      }
      // 'unavailable' — server javob bermadi. Sessiyaga TEGILMAYDI
      // (izoh `sessionRefresh.ts` da), asl xato chaqiruvchiga boradi.
      if (outcome.status === 'rejected') forceLogout()
      return Promise.reject(error)
    }

    // ⚠️ Kirish oqimidagi 401 — "parol/kod noto'g'ri", "sessiya tugadi" EMAS.
    // Sahifa almashtirilmaydi: xato chaqiruvchiga boradi va ekranda
    // ko'rsatiladi (yuqoridagi XSB-005 izohi).
    if (isAuthFlowPath(request?.url ?? '')) return Promise.reject(error)

    if (getToken() === null || isTokenExpired()) forceLogout()
    return Promise.reject(error)
  },
)
